import React, { useState, useEffect } from 'react';
import { talentSearchPreferenceAPI, jobSearchPreferenceAPI, bookmarkAPI } from '../services/api';
import ConfirmationModal from './ConfirmationModal';
import './PreferencesSettings.css';

const PreferencesSettings = () => {
  const [activeTab, setActiveTab] = useState('preferences'); // 'preferences' | 'bookmarks'
  const [talentPreferences, setTalentPreferences] = useState([]);
  const [jobPreferences, setJobPreferences] = useState([]);
  const [talentBookmarks, setTalentBookmarks] = useState([]);
  const [jobBookmarks, setJobBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    type: null, // 'preference-talent', 'preference-job', 'bookmark-talent', 'bookmark-job'
    id: null,
    title: '',
    message: ''
  });
  const [emailNotifications, setEmailNotifications] = useState({
    talentPreferences: true,
    jobPreferences: true,
    talentBookmarks: false,
    jobBookmarks: false
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchTalentPreferences(),
        fetchJobPreferences(),
        fetchTalentBookmarks(),
        fetchJobBookmarks()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTalentPreferences = async () => {
    try {
      const response = await talentSearchPreferenceAPI.getAll();
      if (response.success) {
        setTalentPreferences(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching talent preferences:', error);
    }
  };

  const fetchJobPreferences = async () => {
    try {
      const response = await jobSearchPreferenceAPI.getAll();
      if (response.success) {
        setJobPreferences(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching job preferences:', error);
    }
  };

  const fetchTalentBookmarks = async () => {
    try {
      const response = await bookmarkAPI.getLists('talents');
      if (response.success) {
        setTalentBookmarks(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching talent bookmarks:', error);
    }
  };

  const fetchJobBookmarks = async () => {
    try {
      const response = await bookmarkAPI.getLists('vacancies');
      if (response.success) {
        setJobBookmarks(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching job bookmarks:', error);
    }
  };

  const handleDeletePreference = (id, type) => {
    const preferenceType = type === 'talent' ? 'talent' : 'job';
    setConfirmationModal({
      isOpen: true,
      type: `preference-${type}`,
      id: id,
      title: 'Delete Search Preference',
      message: `Are you sure you want to delete this ${preferenceType} search preference? This action cannot be undone.`
    });
  };

  const confirmDeletePreference = async () => {
    const { id, type } = confirmationModal;
    const preferenceType = type.replace('preference-', '');
    
    setDeleting(prev => ({ ...prev, [`pref-${preferenceType}-${id}`]: true }));
    try {
      const api = preferenceType === 'talent' ? talentSearchPreferenceAPI : jobSearchPreferenceAPI;
      const response = await api.delete(id);
      if (response.success) {
        if (preferenceType === 'talent') {
          setTalentPreferences(prev => prev.filter(p => p.id !== id));
        } else {
          setJobPreferences(prev => prev.filter(p => p.id !== id));
        }
      } else {
        alert(response.message || 'Failed to delete preference. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting preference:', error);
      alert('Failed to delete preference. Please try again.');
    } finally {
      setDeleting(prev => ({ ...prev, [`pref-${preferenceType}-${id}`]: false }));
    }
  };

  const handleDeleteBookmarkList = (id, type) => {
    const bookmarkType = type === 'talent' ? 'talent' : 'job';
    setConfirmationModal({
      isOpen: true,
      type: `bookmark-${type}`,
      id: id,
      title: 'Delete Bookmark List',
      message: `Are you sure you want to delete this ${bookmarkType} bookmark list? All bookmarks in this list will be permanently removed. This action cannot be undone.`
    });
  };

  const confirmDeleteBookmarkList = async () => {
    const { id, type } = confirmationModal;
    const bookmarkType = type.replace('bookmark-', '');
    
    setDeleting(prev => ({ ...prev, [`bookmark-${bookmarkType}-${id}`]: true }));
    try {
      const response = await bookmarkAPI.deleteList(id);
      if (response.success) {
        if (bookmarkType === 'talent') {
          setTalentBookmarks(prev => prev.filter(b => b.id !== id));
        } else {
          setJobBookmarks(prev => prev.filter(b => b.id !== id));
        }
      } else {
        alert(response.message || 'Failed to delete bookmark list. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting bookmark list:', error);
      alert('Failed to delete bookmark list. Please try again.');
    } finally {
      setDeleting(prev => ({ ...prev, [`bookmark-${bookmarkType}-${id}`]: false }));
    }
  };

  const handleConfirmDelete = () => {
    if (confirmationModal.type?.startsWith('preference-')) {
      confirmDeletePreference();
    } else if (confirmationModal.type?.startsWith('bookmark-')) {
      confirmDeleteBookmarkList();
    }
  };

  const handleToggleEmailNotification = async (key, value) => {
    setEmailNotifications(prev => ({ ...prev, [key]: value }));
    // Here you would typically save this to the backend
    // For now, we'll just update the local state
    // TODO: Add API endpoint to save email notification preferences
  };

  const togglePreferenceActive = async (id, type, currentStatus) => {
    try {
      const api = type === 'talent' ? talentSearchPreferenceAPI : jobSearchPreferenceAPI;
      const response = await api.toggleActive(id);
      if (response.success) {
        if (type === 'talent') {
          setTalentPreferences(prev => 
            prev.map(p => p.id === id ? { ...p, is_active: !currentStatus } : p)
          );
        } else {
          setJobPreferences(prev => 
            prev.map(p => p.id === id ? { ...p, is_active: !currentStatus } : p)
          );
        }
      }
    } catch (error) {
      console.error('Error toggling preference:', error);
    }
  };

  if (loading) {
    return (
      <div className="preferences-settings-container">
        <div className="preferences-loading">
          <div className="spinner"></div>
          <p>Loading your preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="preferences-settings-container">
      <div className="preferences-header">
        <h2>Preferences</h2>
        <p>Manage your search preferences, bookmarks, and email notifications</p>
      </div>

      {/* Main Tabs */}
      <div className="preferences-tabs">
        <button
          className={`preferences-tab ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          Search Preferences
        </button>
        <button
          className={`preferences-tab ${activeTab === 'bookmarks' ? 'active' : ''}`}
          onClick={() => setActiveTab('bookmarks')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
          </svg>
          Bookmarks
        </button>
      </div>

      {/* Preferences Tab Content */}
      {activeTab === 'preferences' && (
        <div className="preferences-content">
          {/* Email Notification Toggle */}
          <div className="email-notification-section">
            <div className="notification-header">
              <h3>Email Notifications</h3>
              <p>Get notified when new results match your saved preferences</p>
            </div>
            <div className="notification-options">
              <div className="notification-toggle-wrapper">
                <div className="notification-toggle-content">
                  <div className="notification-toggle-header">
                    <strong>New Talent Matches</strong>
                    <span className="notification-status-badge">
                      {emailNotifications.talentPreferences ? (
                        <span className="status-enabled">✓ Enabled</span>
                      ) : (
                        <span className="status-disabled">○ Disabled</span>
                      )}
                    </span>
                  </div>
                  <span className="notification-description">Receive emails when new talents match your saved preferences</span>
                </div>
                <label className="notification-toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailNotifications.talentPreferences}
                    onChange={(e) => handleToggleEmailNotification('talentPreferences', e.target.checked)}
                  />
                  <span className="notification-slider">
                    <span className="notification-slider-text">
                      {emailNotifications.talentPreferences ? 'ON' : 'OFF'}
                    </span>
                  </span>
                </label>
              </div>
              <div className="notification-toggle-wrapper">
                <div className="notification-toggle-content">
                  <div className="notification-toggle-header">
                    <strong>New Job Matches</strong>
                    <span className="notification-status-badge">
                      {emailNotifications.jobPreferences ? (
                        <span className="status-enabled">✓ Enabled</span>
                      ) : (
                        <span className="status-disabled">○ Disabled</span>
                      )}
                    </span>
                  </div>
                  <span className="notification-description">Receive emails when new jobs match your saved preferences</span>
                </div>
                <label className="notification-toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailNotifications.jobPreferences}
                    onChange={(e) => handleToggleEmailNotification('jobPreferences', e.target.checked)}
                  />
                  <span className="notification-slider">
                    <span className="notification-slider-text">
                      {emailNotifications.jobPreferences ? 'ON' : 'OFF'}
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Talent Preferences */}
          <div className="preferences-section">
            <div className="section-header">
              <h3>Talent Search Preferences</h3>
              <span className="section-count">{talentPreferences.length}</span>
            </div>
            {talentPreferences.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p>No talent search preferences saved yet</p>
                <span>Your saved talent search preferences will appear here</span>
              </div>
            ) : (
              <div className="preferences-list">
                {talentPreferences.map((pref) => (
                  <div key={pref.id} className="preference-item">
                    <div className="preference-content">
                      <div className="preference-header-row">
                        <h4>{pref.title || 'Untitled Preference'}</h4>
                        {pref.is_active && (
                          <span className="badge active">Active</span>
                        )}
                      </div>
                      <div className="preference-details">
                        {pref.search_query && (
                          <span className="detail-tag">Search: {pref.search_query}</span>
                        )}
                        {pref.location && (
                          <span className="detail-tag">Location: {pref.location}</span>
                        )}
                        {pref.work_type && (
                          <span className="detail-tag">Work Type: {pref.work_type}</span>
                        )}
                        {pref.skills && (
                          <span className="detail-tag">Skills: {pref.skills}</span>
                        )}
                      </div>
                    </div>
                    <div className="preference-actions">
                      <div className="toggle-container">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={pref.is_active}
                            onChange={() => togglePreferenceActive(pref.id, 'talent', pref.is_active)}
                          />
                          <span className="slider">
                            <span className="slider-text">{pref.is_active ? 'ON' : 'OFF'}</span>
                          </span>
                        </label>
                        <div className="toggle-label-info">
                          <span className="toggle-label-text">
                            {pref.is_active ? 'Notifications Enabled' : 'Notifications Disabled'}
                          </span>
                          <span className="toggle-label-hint">
                            {pref.is_active 
                              ? 'You\'ll receive email alerts for new matches' 
                              : 'Turn on to get email alerts for new matches'}
                          </span>
                        </div>
                      </div>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeletePreference(pref.id, 'talent')}
                        disabled={deleting[`pref-talent-${pref.id}`]}
                        title="Delete preference"
                      >
                        {deleting[`pref-talent-${pref.id}`] ? (
                          <div className="spinner small"></div>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Job Preferences */}
          <div className="preferences-section">
            <div className="section-header">
              <h3>Job Search Preferences</h3>
              <span className="section-count">{jobPreferences.length}</span>
            </div>
            {jobPreferences.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p>No job search preferences saved yet</p>
                <span>Your saved job search preferences will appear here</span>
              </div>
            ) : (
              <div className="preferences-list">
                {jobPreferences.map((pref) => (
                  <div key={pref.id} className="preference-item">
                    <div className="preference-content">
                      <div className="preference-header-row">
                        <h4>{pref.title || 'Untitled Preference'}</h4>
                        {pref.is_active && (
                          <span className="badge active">Active</span>
                        )}
                      </div>
                      <div className="preference-details">
                        {pref.search_query && (
                          <span className="detail-tag">Search: {pref.search_query}</span>
                        )}
                        {pref.location && (
                          <span className="detail-tag">Location: {pref.location}</span>
                        )}
                        {pref.work_type && (
                          <span className="detail-tag">Work Type: {pref.work_type}</span>
                        )}
                        {pref.job_type && (
                          <span className="detail-tag">Job Type: {pref.job_type}</span>
                        )}
                        {pref.experience_level && (
                          <span className="detail-tag">Experience: {pref.experience_level}</span>
                        )}
                      </div>
                    </div>
                    <div className="preference-actions">
                      <div className="toggle-container">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={pref.is_active}
                            onChange={() => togglePreferenceActive(pref.id, 'job', pref.is_active)}
                          />
                          <span className="slider">
                            <span className="slider-text">{pref.is_active ? 'ON' : 'OFF'}</span>
                          </span>
                        </label>
                        <div className="toggle-label-info">
                          <span className="toggle-label-text">
                            {pref.is_active ? 'Notifications Enabled' : 'Notifications Disabled'}
                          </span>
                          <span className="toggle-label-hint">
                            {pref.is_active 
                              ? 'You\'ll receive email alerts for new matches' 
                              : 'Turn on to get email alerts for new matches'}
                          </span>
                        </div>
                      </div>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeletePreference(pref.id, 'job')}
                        disabled={deleting[`pref-job-${pref.id}`]}
                        title="Delete preference"
                      >
                        {deleting[`pref-job-${pref.id}`] ? (
                          <div className="spinner small"></div>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bookmarks Tab Content */}
      {activeTab === 'bookmarks' && (
        <div className="preferences-content">
          {/* Email Notification Toggle */}
          <div className="email-notification-section">
            <div className="notification-header">
              <h3>Email Notifications</h3>
              <p>Get notified about updates to your bookmarked items</p>
            </div>
            <div className="notification-options">
              <div className="notification-toggle-wrapper">
                <div className="notification-toggle-content">
                  <div className="notification-toggle-header">
                    <strong>Bookmarked Talents</strong>
                    <span className="notification-status-badge">
                      {emailNotifications.talentBookmarks ? (
                        <span className="status-enabled">✓ Enabled</span>
                      ) : (
                        <span className="status-disabled">○ Disabled</span>
                      )}
                    </span>
                  </div>
                  <span className="notification-description">Receive emails about updates to your bookmarked talents</span>
                </div>
                <label className="notification-toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailNotifications.talentBookmarks}
                    onChange={(e) => handleToggleEmailNotification('talentBookmarks', e.target.checked)}
                  />
                  <span className="notification-slider">
                    <span className="notification-slider-text">
                      {emailNotifications.talentBookmarks ? 'ON' : 'OFF'}
                    </span>
                  </span>
                </label>
              </div>
              <div className="notification-toggle-wrapper">
                <div className="notification-toggle-content">
                  <div className="notification-toggle-header">
                    <strong>Bookmarked Jobs</strong>
                    <span className="notification-status-badge">
                      {emailNotifications.jobBookmarks ? (
                        <span className="status-enabled">✓ Enabled</span>
                      ) : (
                        <span className="status-disabled">○ Disabled</span>
                      )}
                    </span>
                  </div>
                  <span className="notification-description">Receive emails about updates to your bookmarked jobs</span>
                </div>
                <label className="notification-toggle-switch">
                  <input
                    type="checkbox"
                    checked={emailNotifications.jobBookmarks}
                    onChange={(e) => handleToggleEmailNotification('jobBookmarks', e.target.checked)}
                  />
                  <span className="notification-slider">
                    <span className="notification-slider-text">
                      {emailNotifications.jobBookmarks ? 'ON' : 'OFF'}
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Talent Bookmarks */}
          <div className="preferences-section">
            <div className="section-header">
              <h3>Talent Bookmark Lists</h3>
              <span className="section-count">{talentBookmarks.length}</span>
            </div>
            {talentBookmarks.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                </svg>
                <p>No talent bookmark lists yet</p>
                <span>Your talent bookmark lists will appear here</span>
              </div>
            ) : (
              <div className="preferences-list">
                {talentBookmarks.map((bookmark) => (
                  <div key={bookmark.id} className="preference-item">
                    <div className="preference-content">
                      <div className="preference-header-row">
                        <h4>{bookmark.title || 'Untitled List'}</h4>
                        <span className="badge count">{bookmark.talents_count || 0} talents</span>
                      </div>
                      {bookmark.description && (
                        <p className="preference-description">{bookmark.description}</p>
                      )}
                    </div>
                    <div className="preference-actions">
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteBookmarkList(bookmark.id, 'talent')}
                        disabled={deleting[`bookmark-talent-${bookmark.id}`]}
                        title="Delete bookmark list"
                      >
                        {deleting[`bookmark-talent-${bookmark.id}`] ? (
                          <div className="spinner small"></div>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Job Bookmarks */}
          <div className="preferences-section">
            <div className="section-header">
              <h3>Job Bookmark Lists</h3>
              <span className="section-count">{jobBookmarks.length}</span>
            </div>
            {jobBookmarks.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                </svg>
                <p>No job bookmark lists yet</p>
                <span>Your job bookmark lists will appear here</span>
              </div>
            ) : (
              <div className="preferences-list">
                {jobBookmarks.map((bookmark) => (
                  <div key={bookmark.id} className="preference-item">
                    <div className="preference-content">
                      <div className="preference-header-row">
                        <h4>{bookmark.title || 'Untitled List'}</h4>
                        <span className="badge count">{bookmark.vacancies_count || 0} jobs</span>
                      </div>
                      {bookmark.description && (
                        <p className="preference-description">{bookmark.description}</p>
                      )}
                    </div>
                    <div className="preference-actions">
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteBookmarkList(bookmark.id, 'job')}
                        disabled={deleting[`bookmark-job-${bookmark.id}`]}
                        title="Delete bookmark list"
                      >
                        {deleting[`bookmark-job-${bookmark.id}`] ? (
                          <div className="spinner small"></div>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={handleConfirmDelete}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default PreferencesSettings;
